
import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useSentimento } from '@/contexts/SentimentoContext';
import { useLanguage } from '@/contexts/LanguageContext';
import WeekStripView, { type WeekStripDay } from '@/components/WeekStripView';

interface SentimentCalendarProps {
  onDayPress?: (date: string) => void;
}

const EMOTION_EMOJIS: { [key: string]: string } = {
  'feliz': '😀',
  'animado': '🤩',
  'tranquilo': '😌',
  'cansado': '😴',
  'frustrado': '😣',
  'triste': '😭',
  'estressado': '😤',
  'irritado': '😠',
  'ansioso': '😰',
};

const PlusIcon = () => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M5 10H15"
      stroke="#A19F97"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M10 5V15"
      stroke="#A19F97"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export default function SentimentCalendar({ onDayPress }: SentimentCalendarProps) {
  const router = useRouter();
  const { t } = useLanguage();
  const { entries } = useSentimento();
  const [days, setDays] = useState<WeekStripDay[]>([]);
  const [weekSentimentCount, setWeekSentimentCount] = useState(0);

  const DAY_NAMES = [
    t('common.weekdays.short.sunday'),
    t('common.weekdays.short.monday'),
    t('common.weekdays.short.tuesday'),
    t('common.weekdays.short.wednesday'),
    t('common.weekdays.short.thursday'),
    t('common.weekdays.short.friday'),
    t('common.weekdays.short.saturday'),
  ];

  useEffect(() => {
    generateCalendarDays();
  }, [entries]);

  const generateCalendarDays = () => {
    const next: WeekStripDay[] = [];
    const today = new Date();
    const todayIso = today.toISOString().split('T')[0];
    let count = 0;

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);

      const dateString = date.toISOString().split('T')[0];
      const dayNumber = date.getDate().toString();
      const dayName = DAY_NAMES[date.getDay()] || '';

      const sentimentEntry = entries.find(entry => entry.entry_date === dateString);
      const emoji = sentimentEntry?.emotion ? EMOTION_EMOJIS[sentimentEntry.emotion] || '' : '';
      if (emoji) count += 1;

      next.push({
        date: dateString,
        dayNumber,
        dayName,
        isToday: dateString === todayIso,
        badge: emoji ? <Text style={styles.emoji}>{emoji}</Text> : <PlusIcon />,
      });
    }

    setDays(next);
    setWeekSentimentCount(count);
  };

  const handleDayPress = (date: string) => {
    if (onDayPress) {
      onDayPress(date);
    } else {
      router.dismiss();
      router.push({
        pathname: '/diario-sentimento',
        params: { selectedDate: date }
      });
    }
  };

  const showCongratulations = weekSentimentCount > 2;

  return (
    <View style={styles.container}>
      {showCongratulations && (
        <View style={styles.congratsRow}>
          <View style={styles.congratsAvatar}>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path
                d="M8.33398 14C11.6479 14 14.334 11.3133 14.334 8C14.334 4.68605 11.6479 2 8.33398 2C5.02004 2 2.33398 4.68605 2.33398 8C2.33398 11.3133 5.02004 14 8.33398 14Z"
                stroke="#151515"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M6.41406 9.52588C6.88174 10.1343 7.56994 10.5137 8.33407 10.5137C9.0982 10.5137 9.7864 10.1343 10.2541 9.52588"
                stroke="#151515"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M6.17969 6.00355V5.96918M6.14934 5.86719C6.06667 5.86719 5.99934 5.93452 6 6.01719C6 6.09985 6.06734 6.16719 6.15001 6.16719C6.23267 6.16719 6.30001 6.09985 6.30001 6.01719C6.30001 5.93385 6.23267 5.86719 6.14934 5.86719Z"
                stroke="#151515"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M10.4878 6.00355V5.96918M10.4833 5.86719C10.4007 5.86719 10.3333 5.93452 10.334 6.01719C10.334 6.09985 10.4013 6.16719 10.484 6.16719C10.5667 6.16719 10.634 6.09985 10.634 6.01719C10.634 5.93385 10.5667 5.86719 10.4833 5.86719Z"
                stroke="#151515"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>
          <Text style={styles.congratsText}>
            <Text style={styles.congratsBold}>{t('calendar.congratsGreeting')}</Text>
            {t('calendar.congratsMiddle')}
            <Text style={styles.congratsBold}>{`${weekSentimentCount} ${t('calendar.congratsCountUnit')}`}</Text>
            {t('calendar.congratsEnd')}
          </Text>
        </View>
      )}

      <WeekStripView days={days} onDayPress={handleDayPress} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingTop: 16,
    gap: 16,
    width: '100%',
  },
  congratsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  congratsAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
    flexShrink: 0,
  },
  congratsText: {
    flex: 1,
    color: '#151515',
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
  },
  congratsBold: {
    fontFamily: 'Inter_700Bold',
  },
  emoji: {
    fontSize: 18,
    lineHeight: 24,
    textAlign: 'center',
  },
});
